/**
 * First-person duelling camera.
 *
 * A bounded stance, not a free-roaming FPS: the player may step within their
 * box and turn, but the arena is not theirs to cross. Position and look angles
 * both come from the input layer via the scene bus — the rig reads them, it
 * never decides them — and it layers breathing, footfalls, recoil, hit
 * reactions and decaying shake on top.
 */
import { useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { MOVEMENT, COMBAT } from '@spell/shared-types';
import { scene as bus } from '../scene/sceneBus';

const EYE = COMBAT.EYE_HEIGHT;

export function FirstPersonRig() {
  const { camera } = useThree();
  const hitKick = useRef(0);
  const lastHp = useRef(bus.selfHp);
  const shakeOffset = useRef(new THREE.Vector3());
  const lastX = useRef(bus.selfX);
  const lastZ = useRef(bus.selfZ);
  const stride = useRef(0);
  const gait = useRef(0);

  useFrame((state, delta) => {
    const t = state.clock.elapsedTime;

    // Camera punch when the player takes a hit.
    if (bus.selfHp < lastHp.current - 0.01) {
      hitKick.current = Math.min(1, hitKick.current + (lastHp.current - bus.selfHp) / 22 + 0.22);
    }
    lastHp.current = bus.selfHp;
    hitKick.current = Math.max(0, hitKick.current - delta * 2.6);

    // Decaying shake, sampled from two out-of-phase sines so it does not read
    // as a regular wobble.
    bus.shake = Math.max(0, bus.shake - delta * 2.2);
    const amp = bus.shake * 0.055 + hitKick.current * 0.05;
    shakeOffset.current.set(
      Math.sin(t * 43.7) * amp + Math.sin(t * 17.3) * amp * 0.5,
      Math.cos(t * 37.1) * amp,
      0,
    );

    // Footfalls, measured from how far the stance actually moved rather than
    // from which keys are down — the position is the server's answer, so the
    // head only bobs when the duellist genuinely travelled. `stride` eases so
    // that starting and stopping are a gait picking up and settling, not a
    // bob switching on.
    const travelled = Math.hypot(bus.selfX - lastX.current, bus.selfZ - lastZ.current);
    lastX.current = bus.selfX;
    lastZ.current = bus.selfZ;
    const pace = delta > 0 ? Math.min(1, travelled / delta / MOVEMENT.SPEED) : 0;
    stride.current += (pace - stride.current) * Math.min(1, delta * 6);
    gait.current += delta * stride.current * 9.5;

    const breathe = Math.sin(t * 1.25) * 0.012;
    const idleSway = Math.sin(t * 0.55) * 0.01 * (1 - stride.current);
    const bobY = Math.sin(gait.current * 2) * 0.028 * stride.current;
    const bobX = Math.sin(gait.current) * 0.022 * stride.current;

    camera.position.set(
      bus.selfX + shakeOffset.current.x + idleSway + bobX,
      EYE + breathe + bobY + shakeOffset.current.y - hitKick.current * 0.06,
      bus.selfZ,
    );

    // Yaw is measured from "facing the opponent", so the base rotation flips
    // with the side the player was assigned. The minus sign lines the camera up
    // with `aimDirection()` on the server — both must agree or shots would land
    // somewhere other than where the crosshair points.
    const baseYaw = bus.selfSide > 0 ? 0 : Math.PI;
    camera.rotation.set(0, 0, 0);
    camera.rotateY(baseYaw - bus.yaw);
    camera.rotateX(bus.pitch - hitKick.current * 0.12);
    camera.rotateZ(Math.sin(t * 0.7) * 0.004 + bus.shake * 0.02);
  });

  return null;
}
